import Vue from 'vue';
import { getCustomisationOptions } from 'js/crossword/getCustomisationOptions';
import { PopupType } from 'js/crossword/html/types';
import { Provider, RevealType } from 'js/crossword/types';
import { trackMenuButton, TopMenuButtonType } from 'js/tracking/track';
import OS from 'framework/utils/OS';
import { formatTimeHMS } from 'js/crossword/helpers/dateHelpers';
import { Puzzle } from 'js/crossword/Puzzle';
import { isCurrentCellRevealed, isCurrentWordRevealed } from 'js/crossword/puzzleDataHandlers/helpers';
import DesktopMenu, { DesktopMenuButtonList } from "./DesktopMenu";
import MobileMenu, { MobileMenuButtonList } from "./MobileMenu";
import TopMenuButton from "./TopMenuButton";

import commonStyle from "./css/common.module.css";
import style from "./css/TopMenu.module.css";
import { ActivityTracker } from '../utils/ActivityTracker';

const INACTIVE_TIMEOUT_THRESHOLD: number = 60000;

enum MenuId {
  NONE = "none",
  HAMBURGER = "hamburger",
  CLEAR = "clear",
  CHECK = "check",
  REVEAL = "reveal",
}

interface IData {
  printButton: boolean,
  calendarButton: boolean,
  desktop: boolean,
  currentMenu: MenuId,
  activityTracker: ActivityTracker | null
}

Vue.component('top-menu-bar', {
  components: {
    DesktopMenu,
    MobileMenu,
    TopMenuButton,
  },
  data(): IData {
    const customisationOptions = getCustomisationOptions();
    return {
      printButton: !customisationOptions.disablePrint,
      calendarButton: !customisationOptions.disableCalendar,
      desktop: OS.desktop,
      currentMenu: MenuId.NONE,
      activityTracker: null
    };
  },
  props: {
    sessionData: Object,
    puzzle: Puzzle,
    settings: Object,
    ui: Object,
  },
  created() {
    this.activityTracker = new ActivityTracker(INACTIVE_TIMEOUT_THRESHOLD, this.pauseOnInactivity);
    window.onblur = this.pauseOnInactivity;
  },

  beforeMount() {
    this.activityTracker?.setupActivityTracker();
  },
  beforeDestroy() {
    this.activityTracker?.stopActivityTracker();
  },
  inject: [Provider.LOCALIZATION],
  computed: {
    timerWidthStyle(): string {
      return `width: ${this.puzzle.data.time >= 3600 * 1000 ? '3.5em' : '2.5em'}`;
    },
  },
  methods: {
    zoomIn() {
      trackMenuButton(TopMenuButtonType.ZoomIn);
      this.$emit('zoomIn');
    },
    zoomOut() {
      trackMenuButton(TopMenuButtonType.ZoomOut);
      this.$emit('zoomOut');
    },
    toggleAutocheck() {
      trackMenuButton(TopMenuButtonType.Autocheck);
      this.puzzle.toggleAutocheck();
    },
    checkLetter() {
      trackMenuButton(TopMenuButtonType.CheckLetter);
      this.puzzle.checkLetter();
    },
    checkWord() {
      trackMenuButton(TopMenuButtonType.CheckWord);
      this.puzzle.checkWord();
    },
    checkPuzzle() {
      trackMenuButton(TopMenuButtonType.CheckPuzzle);
      this.puzzle.checkPuzzle();
    },
    clearWord() {
      trackMenuButton(TopMenuButtonType.ClearWord);
      this.puzzle.clearWord();
    },
    openRevealAdPopup() {
      trackMenuButton(TopMenuButtonType.RevealPuzzle);
      this.ui.popup = { type: PopupType.RevealPuzzleAd, data: null };
    },
    revealLetter() {
      if (isCurrentCellRevealed(this.puzzle.data)) {
        this.ui.popup = { type: PopupType.AlreadyRevealedPopup, data: { revealType: RevealType.Letter } };
        return;
      }

      trackMenuButton(TopMenuButtonType.RevealLetter);
      if (this.sessionData.hintsUsed < 3) {
        this.sessionData.hintsUsed++;
        this.puzzle.revealLetter();
      } else {
        this.ui.popup = { type: PopupType.OutOfHintsAd, data: { revealType: RevealType.Letter } };
      }
    },
    revealWord() {
      if (isCurrentWordRevealed(this.puzzle.data)) {
        this.ui.popup = { type: PopupType.AlreadyRevealedPopup, data: { revealType: RevealType.Word } };
        return;
      }

      trackMenuButton(TopMenuButtonType.RevealWord);
      if (this.sessionData.hintsUsed < 3) {
        this.sessionData.hintsUsed++;
        this.puzzle.revealWord();
      } else {
        this.ui.popup = { type: PopupType.OutOfHintsAd, data: { revealType: RevealType.Word } };
      }
    },
    formatTime() {
      return formatTimeHMS(Math.floor(this.puzzle.getTime() / 1000));
    },
    toggleMenu(id?: MenuId) {
      this.currentMenu = id && this.currentMenu !== id ? id : MenuId.NONE;
    },
    openClearPuzzlePopup(withTimer: boolean) {
      trackMenuButton(withTimer ? TopMenuButtonType.ClearPuzzleAndTimer : TopMenuButtonType.ClearPuzzle);
      this.currentMenu = MenuId.NONE;
      this.ui.popup = { type: PopupType.ClearPuzzle, data: { withTimer } };
    },
    openPrintPopup() {
      trackMenuButton(TopMenuButtonType.Print);
      this.currentMenu = MenuId.NONE;
      this.ui.popup = { type: PopupType.Print, data: null };
    },
    openPausePopup() {
      trackMenuButton(TopMenuButtonType.Pause);
      this.currentMenu = MenuId.NONE;
      this.ui.popup = { type: PopupType.Pause, data: null };
    },
    openCalendar() {
      trackMenuButton(TopMenuButtonType.Calendar);
      this.currentMenu = MenuId.NONE;
      this.ui.popup = { type: PopupType.Calendar, data: null };
    },
    openSettings() {
      trackMenuButton(TopMenuButtonType.Settings);
      this.currentMenu = MenuId.NONE;
      this.ui.popup = { type: PopupType.Settings, data: null };
    },
    pauseOnInactivity() {
      // if there is already some menu popup present, do not open the pause menu
      if (this.ui.popup === null) {
        this.openPausePopup();
      }
    }
  },
  render() {
    const hamburgerMenuMobileOptions: MobileMenuButtonList = [];
    if (this.calendarButton) hamburgerMenuMobileOptions.push({
      label: this.localization.get("Calendar"),
      imgSrc: "images/Calendar_Black_Icon.svg",
      onClick: this.openCalendar,
    });

    hamburgerMenuMobileOptions.push({
      label: this.localization.get("Settings"),
      imgSrc: "images/Settings_Black.svg",
      onClick: this.openSettings,
    });

    if (this.printButton) hamburgerMenuMobileOptions.push({
      label: this.localization.get("Print"),
      imgSrc: "images/Print_Icon.svg",
      onClick: this.openPrintPopup,
    });

    const getLabel = (mobilePrefix: string, text: string): string => {
      const prefix = OS.desktop ? "" : mobilePrefix + " ";
      return prefix + text;
    };


    const clearText = this.localization.get("Clear");
    const clearMenuOptions: MobileMenuButtonList = [{
      label: getLabel(clearText, this.localization.get("Word")),
      onClick: () => this.clearWord(),
    }, {
      label: getLabel(clearText, this.localization.get("Puzzle")),
      onClick: () => this.openClearPuzzlePopup(false),
    }, {
      label: getLabel(clearText, this.localization.get("Puzzle & Timer")),
      onClick: () => this.openClearPuzzlePopup(true),
    }];

    const checkText = this.localization.get("Check");
    const checkMenuOptions: MobileMenuButtonList = [{
      label: this.localization.get("Autocheck"),
      onClick: () => this.toggleAutocheck(),
      highlight: this.puzzle.data.autocheck,
    }, {
      label: getLabel(checkText, this.localization.get("Letter")),
      onClick: () => this.checkLetter(),
    }, {
      label: getLabel(checkText, this.localization.get("Word")),
      onClick: () => this.checkWord(),
    }, {
      label: getLabel(checkText, this.localization.get("Puzzle")),
      onClick: () => this.checkPuzzle(),
    }];

    const revealText = this.localization.get("Reveal");
    const revealMenuOptions: MobileMenuButtonList = [{
      label: getLabel(revealText, this.localization.get("Letter")),
      onClick: () => this.revealLetter(),
    }, {
      label: getLabel(revealText, this.localization.get("Word")),
      onClick: () => this.revealWord(),
    }, {
      label: getLabel(revealText, this.localization.get("Puzzle")),
      onClick: () => this.openRevealAdPopup(),
    }];

    return (
      <div class={OS.desktop ? style.container : style.containerMobile}>
        {/* Mobile menus */}
        { !OS.desktop ? (
          <div>
            <div
              onClick={() => this.toggleMenu()}
              class={this.currentMenu !== MenuId.NONE ? style.clickableBg : ""}>
            </div>
            <div>
              <MobileMenu open={this.currentMenu === MenuId.HAMBURGER} options={hamburgerMenuMobileOptions} />
              <MobileMenu open={this.currentMenu === MenuId.CLEAR} options={clearMenuOptions} />
              <MobileMenu open={this.currentMenu === MenuId.CHECK} options={checkMenuOptions} />
              <MobileMenu open={this.currentMenu === MenuId.REVEAL} options={revealMenuOptions} />
            </div>
          </div>
        ) : null}

        <div class={style.background}>
          {this.settings.progressBar
            ? <div class={style.fillbar} style={{ width: `${this.puzzle.data.fillRate * 100}%` }}></div>
            : null
          }

          <div class={OS.desktop ? style.content : style.contentMobile}>
            {this.settings.timer
              ? <button class={style.menuButton} onClick={() => this.openPausePopup()}>
                <div class={[
                  commonStyle.buttonLabel,
                  !OS.desktop ? style.buttonLabelMobileTimer : ""
                ]}>
                  <img class={commonStyle.labelIcon} src="images/Pause_Icon.svg" />
                  <div class={style.labelTimer} /*style={this.timerWidthStyle}*/>
                    {this.formatTime()}
                  </div>
                </div>
              </button>
              : null
            }

            <div class={OS.desktop ? style.middleButtonsWrapper : style.middleButtonsWrapperMobile}>
              <TopMenuButton
                label={revealText}
                imgSrc="images/Lightbulb_Icon.svg"
                clickHandler={() => { !OS.desktop && this.toggleMenu(MenuId.REVEAL) }}
                hoverHandler={() => { OS.desktop && this.toggleMenu(MenuId.REVEAL) }}>
                {OS.desktop
                  ? <DesktopMenu open={this.currentMenu === MenuId.REVEAL} options={revealMenuOptions} />
                  : null}
              </TopMenuButton>

              <TopMenuButton
                label={checkText}
                imgSrc="images/Check_Icon.svg"
                clickHandler={() => { !OS.desktop && this.toggleMenu(MenuId.CHECK) }}
                hoverHandler={() => { OS.desktop && this.toggleMenu(MenuId.CHECK) }}>
                {OS.desktop
                  ? <DesktopMenu open={this.currentMenu === MenuId.CHECK} options={checkMenuOptions} />
                  : null}
              </TopMenuButton>

              <TopMenuButton
                label={clearText}
                imgSrc="images/Undo_Icon.svg"
                clickHandler={() => { !OS.desktop && this.toggleMenu(MenuId.CLEAR) }}
                hoverHandler={() => { OS.desktop && this.toggleMenu(MenuId.CLEAR) }}>
                {OS.desktop
                  ? <DesktopMenu open={this.currentMenu === MenuId.CLEAR} options={clearMenuOptions} />
                  : null}
              </TopMenuButton>

              {
                OS.desktop ? (
                  <div class={[style.zoomWrapper, style.item]}>
                    <div class={style.zoomContainer}>
                      <img class={style.zoomIcon} src="images/Zoom_Icon.svg" />
                      <button class={style.menuButton} onClick={this.zoomIn}>
                        <div class={commonStyle.buttonLabel}>
                          <div class={style.labelText}>+</div>
                        </div>
                      </button>
                      <div class={style.zoomIconSeparator}></div>
                      <button class={style.menuButton} onClick={this.zoomOut}>
                        <div class={commonStyle.buttonLabel}>
                          <div class={style.labelText}>-</div>
                        </div>
                      </button>
                    </div>
                  </div>
                ) : null
              }
            </div>

            {
              !OS.desktop ? (
                <div class={style.buttonRight}>
                  <button class={style.hamburgerButton} onClick={() => this.toggleMenu(MenuId.HAMBURGER)}>
                    <div class={commonStyle.buttonLabel}>
                      <img
                        class={commonStyle.labelIcon}
                        src={this.currentMenu === MenuId.HAMBURGER
                          ? "images/Cross_Grey.svg"
                          : "images/Hamburger_Icon.svg"}
                      />
                    </div>
                  </button>
                </div>
              ) : (
                <div class={style.buttonRight}>
                  {hamburgerMenuMobileOptions.map(item => (
                    <button class={style.menuButton} onClick={item.onClick}>
                      <div class={commonStyle.buttonLabel}>
                        <img class={style.labelSingleIcon} src={item.imgSrc} />
                      </div>
                    </button>
                  ))}
                </div>
              )
            }
          </div>
        </div>
      </div>
    );
  },
});
